import { useState } from 'react';

import moment from 'moment-timezone';
import { WidgetView, WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useBattleCardsStore } from 'store/battlecards.store';

import { Payload } from 'api/endPoints/getPivotTable';
import { useMutationExtractPivotTableToCsv } from 'api/mutations/extractPivotTableToCsv.mutation';
import { useQueryGetPivotTable } from 'api/queries/getPivotTable.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { DisplayStyle } from 'components/common/ToggleView/ToggleView';

import 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.types';
import { createSpecialFiltersPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';

export type UseGetPivotTableProvider = ReturnType<typeof useGetPivotTableProvider>;

const INITIAL_PRIMARY_GROUP_SELECT = { value: 'wbsLevel1', label: 'WBS Level 1' };
const INITIAL_SECONDARY_GROUP_SELECT = { value: 'wbsLevel2', label: 'WBS Level 2' };

interface Props {
    latestVersionId?: number;
    widgetId?: string;
    projectId?: number | null;
    contractId?: number | null;
    localFilters: any;
    globalFilterData: any;
    endpoints: Record<string, string>;
    widgetViewMode: WidgetViewMode;
}

export const useGetPivotTableProvider = ({
    latestVersionId,
    globalFilterData,
    projectId,
    widgetId,
    contractId,
    localFilters,
    endpoints,
    widgetViewMode = WidgetView.page,
}: Props) => {
    function setFilters(source: any) {
        if (!projectId || !contractId || !latestVersionId) return { filtersArray: [], flags: {} };

        const baseFilters = getFiltersData({
            [FilterNames.PROJECT_CARD_BOARD]: [projectId],
            [FilterNames.CONTRACT_CARD_BOARD]: [contractId],
            [FilterNames.VERSION_CARD_BOARD]: [latestVersionId],
            [FilterNames.ACTIVITY_TYPE_BOARD]: source?.cardActivity,
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.ASSIGNEE_CARD_BOARD]: source?.assignee,
            [FilterNames.IMPORTANT_CARD_BOARD]: source?.important,
            [FilterNames.URGENT_TYPE_CARD_BOARD]: source?.urgent,
            [FilterNames.FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD]: source?.completionPlanSegment,
            [FilterNames.DOCLINK_CARD_BOARD]: source?.hasAttachments,
            [FilterNames.LAG_CARD_BOARD]: source?.lagLead,
            [FilterNames.TRACKED_CARD_BOARD]: source?.trackedActivity,
            [FilterNames.FRAGNET_FORWARD_SEARCH]: source?.activityForwardFragnet,
            [FilterNames.TYPE_CARD_BOARD]: source?.cardType,
            [FilterNames.CLUSTER_CARD_BOARD]: source?.cluster,
            [FilterNames.DURATION_CARD_BOARD]: source?.duration,
            [FilterNames.TOTAL_FLOAT_CARD_BOARD]: source?.float,
            [FilterNames.ACTIVITY_NAME_CARD_BOARD]: source?.relatedActivity,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: source?.resourceName,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: source?.resourceType,
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: source?.submittedCompletion,
            [FilterNames.UPDATED_USER_CARD_BOARD]: source?.updatedBy,
            [FilterNames.WBS_LEVEL_CARD_BOARD]: source?.wbsLevel,
            [FilterNames.STATUS_CARD_BOARD]: source.status,
        });

        const specialFiltersPayload = createSpecialFiltersPayload(source);
        const specialFilters = specialFiltersPayload?.filters ?? [];
        const flags = specialFiltersPayload?.flags ?? {};

        return { filtersArray: [...baseFilters, ...specialFilters], flags };
    }

    const displayStyle = useBattleCardsStore((state) => state.displayStyle);
    const isInReportViewMode = WidgetViewModeUtils.isReportView(widgetViewMode);

    const [primaryGroupSelected, setPrimaryGroupSelected] = useState<ISelectOption<string>>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.primaryGroupSelected
            : INITIAL_PRIMARY_GROUP_SELECT,
    );
    const [secondaryGroupSelected, setSecondaryGroupSelected] = useState<ISelectOption<string> | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.secondaryGroupSelected
            : INITIAL_SECONDARY_GROUP_SELECT,
    );

    const source = isInReportViewMode
        ? { ...localFilters }
        : { ...globalFilterData, displayStyle, primaryGroupSelected, secondaryGroupSelected };

    let filters = setFilters(source);

    // create pill data for PinboardWidgetPills component( for report)
    const pillsData = {
        primaryGroupBy: source?.primaryGroupSelected.label,
        secondaryGroupBy: source?.secondaryGroupSelected ? source?.secondaryGroupSelected.label : undefined,
        cardActivity: source?.cardActivity,
        assignee: source?.assignee,
        important: source?.important,
        urgent: source?.urgent,
        completionPlanSegment: source?.completionPlanSegment,
        hasAttachments: source?.hasAttachments,
        lagLead: source?.lagLead,
        trackedActivity: source?.trackedActivity,
        activityForwardFragnet: source?.activityForwardFragnet,
        cardType: source?.cardType,
        cluster: source?.cluster,
        duration: source?.duration,
        float: source?.float,
        relatedActivity: source?.relatedActivity,
        resourceName: source?.resourceName,
        submittedCompletion: source?.submittedCompletion,
        updatedBy: source?.updatedBy,
        wbsLevel: source?.wbsLevel,
        status: source?.status,
        resourceType: source?.resourceType,
        milestoneFragnet: source?.milestoneFragnet,
        wbs: source?.wbs,
        finishDate: source?.finishDate,
        startDate: source?.startDate,
        baselineFinishDate: source?.baselineFinishDate,
        baselineStartDate: source?.baselineStartDate,
        hasComments: source?.hasComments,
        riskIndicators: source?.riskIndicators,
        searchByName: source?.searchByName,
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    const getPivotTableDataPayload = (): Payload => {
        return {
            cardCachedFilter: {
                filter: {
                    filters: filters.filtersArray,
                    flags: filters.flags,
                },
            },
            paging: { size: 100, page: 0 },
            primaryGroupField: source?.primaryGroupSelected.value,
            secondaryGroupField: source?.secondaryGroupSelected?.value ?? null,
            currentDay: moment().local().format('YYYY-MM-DD'),
        };
    };

    const payload = getPivotTableDataPayload();
    const isPivotTableViewShouldBeDisplayed =
        WidgetViewModeUtils.isInAnyReportMode(widgetViewMode) ||
        (WidgetViewModeUtils.isPageView(widgetViewMode) && displayStyle === DisplayStyle.PIVOT);

    const getPivotTableQuery = useQueryGetPivotTable(
        payload,
        endpoints.getPivotTable,
        isPivotTableViewShouldBeDisplayed,
    );

    const mutateExtractPivotTableToCsv = useMutationExtractPivotTableToCsv();

    const getState = () => {
        return {
            primaryGroupSelected: source.primaryGroupSelected,
            secondaryGroupSelected: source.secondaryGroupSelected,
            isPivotTableViewSelected: isPivotTableViewShouldBeDisplayed,
            isGroupByTableLoading: getPivotTableQuery.isLoading,
            groupedByTableFlatData: getPivotTableQuery.data,
            cardActivity: source?.cardActivity,
            assignee: source?.assignee,
            important: source?.important,
            urgent: source?.urgent,
            completionPlanSegment: source?.completionPlanSegment,
            hasAttachments: source?.hasAttachments,
            lagLead: source?.lagLead,
            trackedActivity: source?.trackedActivity,
            activityForwardFragnet: source?.activityForwardFragnet,
            cardType: source?.cardType,
            clusterType: source?.clusterType,
            duration: source?.duration,
            float: source?.float,
            relatedActivity: source?.relatedActivity,
            resourceName: source?.resourceName,
            submittedCompletion: source?.submittedCompletion,
            updatedBy: source?.updatedBy,
            wbsLevel: source?.wbsLevel,
            status: source?.status,
            resourceType: source?.resourceType,
            activityCodes: source?.activityCodes,
            milestoneFragnet: source?.milestoneFragnet,
            tags: source?.tags,
            wbs: source?.wbs,
            finishDate: source?.finishDate,
            startDate: source?.startDate,
            baselineFinishDate: source?.baselineFinishDate,
            baselineStartDate: source?.baselineStartDate,
            hasComments: source?.hasComments,
            riskIndicators: source?.riskIndicators,
            searchByName: source?.searchByName,
            displayStyle: source?.displayStyle,
        };
    };

    return {
        ui: {
            state: getState(),
            setters: {
                setPrimaryGroupSelected,
                setSecondaryGroupSelected,
            },
            pills: pills,
        },
        api: {
            getPivotTable: {
                instance: getPivotTableQuery,
                payload,
                endpoint: endpoints.getPivotTable,
            },
            exportPivotTableToCsv: {
                instance: mutateExtractPivotTableToCsv,
                payload,
                endpoint: endpoints.exportPivotTableToCsv,
            },
        },
    };
};
